import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  MapPin,
  Phone,
  Mail,
  TrendingUp,
  Users,
  Calendar,
  Building2,
  Plus,
  X,
  UserPlus,
  Trash2,
} from 'lucide-react';
import apiService from './services/api';
import { Panel, Badge, Button, LoadingSpinner } from './components';

function AlfPage() {
  // Data state
  const [facilities, setFacilities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name-asc');

  // Create/Edit modal state
  const [showForm, setShowForm] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', address: '', city: '', state: 'FL', zip_code: '', notes: '', location_id: '',
  });
  const [saving, setSaving] = useState(false);

  // Expanded facility (inline detail)
  const [expandedId, setExpandedId] = useState(null);
  const [facilityContacts, setFacilityContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Contact form
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactFormData, setContactFormData] = useState({ name: '', role: '', phone: '', email: '' });
  const [savingContact, setSavingContact] = useState(false);

  // Fetch facilities
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (locationFilter !== 'all') params.location = locationFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;

      const [facData, locData] = await Promise.all([
        apiService.getAlfFacilities(params),
        apiService.getLocations(),
      ]);

      setFacilities(Array.isArray(facData) ? facData : facData.results || []);
      setLocations(Array.isArray(locData) ? locData : locData.results || []);
    } catch (err) {
      setError(err.message || 'Failed to load ALF data');
      console.error('Error fetching ALF data:', err);
    } finally {
      setLoading(false);
    }
  }, [locationFilter, statusFilter, searchTerm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch contacts when expanding a facility
  const handleToggleExpand = useCallback(async (facilityId) => {
    if (expandedId === facilityId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(facilityId);
    setLoadingContacts(true);
    try {
      const data = await apiService.getAlfFacilityContacts(facilityId);
      setFacilityContacts(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setFacilityContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }, [expandedId]);

  // Sort client-side
  const sortedFacilities = useMemo(() => {
    return [...facilities].sort((a, b) => {
      switch (sortBy) {
        case 'revenue-desc':
          return (b.total_revenue || 0) - (a.total_revenue || 0);
        case 'revenue-asc':
          return (a.total_revenue || 0) - (b.total_revenue || 0);
        case 'visits-desc':
          return (b.total_appointments || 0) - (a.total_appointments || 0);
        case 'residents-desc':
          return (b.resident_count || 0) - (a.resident_count || 0);
        case 'name-asc':
          return (a.name || '').localeCompare(b.name || '');
        default:
          return 0;
      }
    });
  }, [facilities, sortBy]);

  // Aggregate stats
  const aggregateStats = useMemo(() => {
    const totalRevenue = facilities.reduce((sum, f) => sum + (f.total_revenue || 0), 0);
    const totalVisits = facilities.reduce((sum, f) => sum + (f.total_appointments || 0), 0);
    const totalResidents = facilities.reduce((sum, f) => sum + (f.resident_count || 0), 0);
    const activeFacilities = facilities.filter(f => f.status === 'active').length;
    return { totalRevenue, totalVisits, totalResidents, activeFacilities, total: facilities.length };
  }, [facilities]);

  const formatCurrency = (val) => {
    if (!val) return '$0';
    if (val >= 1000) return `$${(val / 1000).toFixed(1)}k`;
    return `$${val.toFixed(0)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Form handlers
  const openCreateForm = () => {
    setEditingFacility(null);
    setFormData({ name: '', phone: '', email: '', address: '', city: '', state: 'FL', zip_code: '', notes: '', location_id: '' });
    setShowForm(true);
  };

  const openEditForm = (facility) => {
    setEditingFacility(facility);
    setFormData({
      name: facility.name || '',
      phone: facility.phone || '',
      email: facility.email || '',
      address: facility.address || '',
      city: facility.city || '',
      state: facility.state || 'FL',
      zip_code: facility.zip_code || '',
      notes: facility.notes || '',
      location_id: facility.location || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.location_id) delete payload.location_id;
      if (editingFacility) {
        await apiService.updateAlfFacility(editingFacility.id, payload);
      } else {
        await apiService.createAlfFacility(payload);
      }
      setShowForm(false);
      fetchData();
    } catch (err) {
      console.error('Error saving facility:', err);
    } finally {
      setSaving(false);
    }
  };

  // Contact handlers
  const handleSaveContact = async () => {
    if (!contactFormData.name.trim() || !expandedId) return;
    setSavingContact(true);
    try {
      await apiService.createAlfFacilityContact({ ...contactFormData, facility: expandedId });
      setShowContactForm(false);
      setContactFormData({ name: '', role: '', phone: '', email: '' });
      // Refresh contacts
      const data = await apiService.getAlfFacilityContacts(expandedId);
      setFacilityContacts(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error('Error saving contact:', err);
    } finally {
      setSavingContact(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await apiService.deleteAlfFacilityContact(contactId);
      setFacilityContacts(prev => prev.filter(c => c.id !== contactId));
    } catch (err) {
      console.error('Error deleting contact:', err);
    }
  };

  if (loading && facilities.length === 0) {
    return (
      <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
        <div className="p-3">
          <Panel className="p-6"><LoadingSpinner /></Panel>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
        <div className="p-3">
          <Panel className="bg-red-50 border-red-200">
            <h3 className="text-xs font-semibold text-red-800">Error Loading ALF Data</h3>
            <p className="text-xs text-red-600 mt-1">{error}</p>
            <Button onClick={fetchData} size="sm" className="mt-2 bg-red-600 hover:bg-red-700">Retry</Button>
          </Panel>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Header */}
        <div className="mb-3 flex justify-between items-center">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 leading-none">Assisted Living Facilities</h1>
            <p className="text-xs text-gray-500 mt-1">Manage ALF facilities, contacts, and revenue</p>
          </div>
          <Button size="sm" onClick={openCreateForm}>
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Facility
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
          <Panel className="!p-2 text-center">
            <div className="text-lg font-bold text-gray-900">{aggregateStats.total}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Facilities</div>
          </Panel>
          <Panel className="!p-2 text-center">
            <div className="text-lg font-bold text-green-700">{aggregateStats.activeFacilities}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Active</div>
          </Panel>
          <Panel className="!p-2 text-center">
            <div className="text-lg font-bold text-purple-700">{aggregateStats.totalResidents}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Residents</div>
          </Panel>
          <Panel className="!p-2 text-center">
            <div className="text-lg font-bold text-blue-700">{aggregateStats.totalVisits}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Total Visits</div>
          </Panel>
          <Panel className="!p-2 text-center">
            <div className="text-lg font-bold text-emerald-700">{formatCurrency(aggregateStats.totalRevenue)}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Revenue</div>
          </Panel>
        </div>

        {/* Filters */}
        <Panel className="mb-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search facilities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent"
              />
            </div>

            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
            >
              <option value="all">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
            >
              <option value="name-asc">Name A–Z</option>
              <option value="revenue-desc">Revenue (High to Low)</option>
              <option value="revenue-asc">Revenue (Low to High)</option>
              <option value="visits-desc">Most Visits</option>
              <option value="residents-desc">Most Residents</option>
            </select>
          </div>
        </Panel>

        {/* Facilities List */}
        <div className="space-y-2">
          {sortedFacilities.length === 0 && !loading && (
            <Panel className="text-center py-6">
              <Building2 className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-xs text-gray-500">No ALF facilities found</p>
              <Button size="sm" className="mt-2" onClick={openCreateForm}>
                <Plus className="w-3 h-3 mr-1" />
                Add your first facility
              </Button>
            </Panel>
          )}

          {sortedFacilities.map(facility => {
            const isExpanded = expandedId === facility.id;

            return (
              <Panel key={facility.id} className="transition-colors">
                <div
                  className="flex items-start justify-between gap-3 cursor-pointer"
                  onClick={() => handleToggleExpand(facility.id)}
                >
                  {/* Left: Facility Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {facility.name}
                      </h3>
                      <Badge variant={facility.status === 'active' ? 'success' : 'secondary'}>
                        {facility.status === 'active' ? 'Active' : 'Inactive'}
                      </Badge>
                      {facility.location_name && (
                        <span className="text-[10px] text-gray-400">{facility.location_name}</span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500 mb-2">
                      {(facility.address || facility.city) && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {[facility.address, facility.city, facility.state].filter(Boolean).join(', ')}
                        </span>
                      )}
                      {facility.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {facility.phone}
                        </span>
                      )}
                      {facility.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {facility.email}
                        </span>
                      )}
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="bg-purple-50 rounded px-2 py-1">
                        <div className="flex items-center gap-1 text-[10px] text-purple-600 uppercase tracking-wide">
                          <Users className="w-3 h-3" />
                          Residents
                        </div>
                        <div className="text-sm font-bold text-purple-800">{facility.resident_count || 0}</div>
                      </div>
                      <div className="bg-blue-50 rounded px-2 py-1">
                        <div className="flex items-center gap-1 text-[10px] text-blue-600 uppercase tracking-wide">
                          <Calendar className="w-3 h-3" />
                          Visits
                        </div>
                        <div className="text-sm font-bold text-blue-800">{facility.total_appointments || 0}</div>
                      </div>
                      <div className="bg-emerald-50 rounded px-2 py-1">
                        <div className="flex items-center gap-1 text-[10px] text-emerald-600 uppercase tracking-wide">
                          <TrendingUp className="w-3 h-3" />
                          Revenue
                        </div>
                        <div className="text-sm font-bold text-emerald-800">{formatCurrency(facility.total_revenue)}</div>
                      </div>
                      <div className="bg-gray-50 rounded px-2 py-1">
                        <div className="text-[10px] text-gray-500 uppercase tracking-wide">Last Visit</div>
                        <div className="text-[11px] font-semibold text-gray-700">{formatDate(facility.last_visit_date)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Edit button */}
                  <button
                    className="text-xs text-gray-400 hover:text-red-800 mt-1"
                    onClick={(e) => { e.stopPropagation(); openEditForm(facility); }}
                  >
                    Edit
                  </button>
                </div>

                {/* Expanded: Contacts */}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Contacts</h4>
                      <button
                        className="flex items-center gap-1 text-[10px] text-red-800 hover:text-red-900 font-medium"
                        onClick={() => { setShowContactForm(true); setContactFormData({ name: '', role: '', phone: '', email: '' }); }}
                      >
                        <UserPlus className="w-3 h-3" />
                        Add Contact
                      </button>
                    </div>

                    {loadingContacts ? (
                      <div className="py-2 text-center text-[10px] text-gray-400">Loading...</div>
                    ) : facilityContacts.length === 0 ? (
                      <div className="py-2 text-center text-[10px] text-gray-400">No contacts yet</div>
                    ) : (
                      <div className="space-y-1.5">
                        {facilityContacts.map(contact => (
                          <div key={contact.id} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5">
                            <div>
                              <span className="text-xs font-medium text-gray-800">{contact.name}</span>
                              {contact.role && (
                                <span className="text-[10px] text-gray-500 ml-1.5">({contact.role})</span>
                              )}
                              <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-0.5">
                                {contact.phone && <span>{contact.phone}</span>}
                                {contact.email && <span>{contact.email}</span>}
                              </div>
                            </div>
                            <button
                              className="text-gray-300 hover:text-red-500"
                              onClick={() => handleDeleteContact(contact.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Inline contact form */}
                    {showContactForm && (
                      <div className="mt-2 bg-gray-50 rounded p-2 space-y-1.5">
                        <div className="grid grid-cols-2 gap-1.5">
                          <input
                            type="text"
                            placeholder="Name *"
                            value={contactFormData.name}
                            onChange={(e) => setContactFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                          />
                          <input
                            type="text"
                            placeholder="Role (e.g. Director)"
                            value={contactFormData.role}
                            onChange={(e) => setContactFormData(prev => ({ ...prev, role: e.target.value }))}
                            className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                          />
                          <input
                            type="text"
                            placeholder="Phone"
                            value={contactFormData.phone}
                            onChange={(e) => setContactFormData(prev => ({ ...prev, phone: e.target.value }))}
                            className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                          />
                          <input
                            type="email"
                            placeholder="Email"
                            value={contactFormData.email}
                            onChange={(e) => setContactFormData(prev => ({ ...prev, email: e.target.value }))}
                            className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                          />
                        </div>
                        <div className="flex gap-1.5">
                          <Button size="sm" onClick={handleSaveContact} disabled={savingContact || !contactFormData.name.trim()}>
                            {savingContact ? 'Saving...' : 'Save Contact'}
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => setShowContactForm(false)}>Cancel</Button>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {facility.notes && (
                      <div className="mt-2 text-[10px] text-gray-500">
                        <span className="font-semibold text-gray-600">Notes:</span> {facility.notes}
                      </div>
                    )}
                  </div>
                )}
              </Panel>
            );
          })}
        </div>

        {sortedFacilities.length > 0 && (
          <div className="mt-3 text-center text-[10px] text-gray-400">
            {sortedFacilities.length} facilit{sortedFacilities.length === 1 ? 'y' : 'ies'}
          </div>
        )}
      </div>

      {/* Create/Edit Facility Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">
                {editingFacility ? 'Edit Facility' : 'New ALF Facility'}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                  placeholder="Facility name"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Zip</label>
                  <input
                    type="text"
                    value={formData.zip_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Location</label>
                <select
                  value={formData.location_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, location_id: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
                >
                  <option value="">No location</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-100">
              <Button size="sm" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving || !formData.name.trim()}>
                {saving ? 'Saving...' : (editingFacility ? 'Update' : 'Create')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AlfPage;
